import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast, Toaster } from 'react-hot-toast';
import ArrowLeft from './images/ArrowLeft.png';
import { faImage } from '@fortawesome/free-solid-svg-icons';

/* ------------------------------ Helpers ------------------------------ */
const formatDate = (isoString) => {
  if (!isoString) return null;
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

// compute latest created_at ISO string or null
const computeLatestCreatedAtFromMoqs = (moqs = []) => {
  if (!Array.isArray(moqs) || moqs.length === 0) return null;
  const validDates = moqs
    .map(m => m?.created_at ?? m?.createdAt ?? null)
    .filter(Boolean)
    .map(s => new Date(s))
    .filter(d => !Number.isNaN(d.getTime()));
  if (validDates.length === 0) return null;
  const latest = validDates.reduce((a, b) => (b > a ? b : a));
  return latest.toISOString();
};

// compute lowest selling price > 0; return number or null if none
const computeLowestSellingPrice = (moqs = []) => {
  const valid = moqs
    .map(r => {
      // treat empty string as NaN here for vendor price calc (we only accept > 0)
      const num = r?.selling_price === '' || r?.selling_price == null ? NaN : Number(r.selling_price);
      return Number.isFinite(num) ? num : NaN;
    })
    .filter(v => !isNaN(v) && v > 0); // strictly greater than 0
  return valid.length ? Math.min(...valid) : null;
};

/* ------------------------------ Component ------------------------------ */

export default function UpdatePrice() {
  const location = useLocation();
  const navigate = useNavigate();
  const fetchUrl = process.env.REACT_APP_FORE_BRICKBUCKET;
  const ImgUrl = process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES;

  const { categoryData } = location.state || {};

  const [items, setItems] = useState([]);
  const [savingItems, setSavingItems] = useState(new Set());
  const [changedItems, setChangedItems] = useState(new Set());
const [customPrice, setCustomPrice] = useState("");
  useEffect(() => {
    FetchProductsbyCategory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const FetchProductsbyCategory = async () => {
    try {
      const vendorDataString = localStorage.getItem('vendorData');
      if (!vendorDataString) throw new Error('Vendor data not found.');

      const vendorData = JSON.parse(vendorDataString);
      if (!vendorData?.id) throw new Error('Invalid vendor data.');

      const res = await fetch(
        `${fetchUrl}product/get-quick-buy-products-by-vendor-category?vendor_id=${vendorData.id}&product_category_id=${categoryData?._id}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', 'x-token': vendorData.token },
        }
      );

      const data = await res.json();

      const initializedItems = (data?.data?.products || []).map(product => {
        const vendorDetails = product.vendor_details || {};
        const moqsFromVendor = Array.isArray(vendorDetails.moqs)
          ? vendorDetails.moqs.map(moq => ({
              min_quantity: moq.min_quantity ?? moq.min ?? '',
              max_quantity: moq.max_quantity ?? moq.max ?? '',
              selling_price: moq.selling_price != null ? String(moq.selling_price) : '',
              cost_price: moq.cost_price != null ? String(moq.cost_price) : '',
              created_at: moq.created_at ?? moq.createdAt ?? null,
            }))
          : [];

        const latestIso = computeLatestCreatedAtFromMoqs(moqsFromVendor);
        const latestFormatted = formatDate(latestIso);

        // compute vendor price from moqs > 0, or use vendorDetails.vendor_price if present
        const lowest = computeLowestSellingPrice(moqsFromVendor);
        const vendorPriceComputed = lowest != null ? lowest : (vendorDetails.vendor_price ?? null);

        return {
          ...product,
          id: product._id,
          vendor_details: { ...vendorDetails, vendor_price: vendorPriceComputed },
          ranges: moqsFromVendor,
          latest_updated_date_iso: latestIso,
          latest_updated_date: latestFormatted,
        };
      });

      setItems(initializedItems);
    } catch (err) {
      console.error('Fetch error', err);
      setItems([]);
      toast.error('Error fetching products');
    }
  };

  // Handler: update selling_price (string allowed). Empty string is allowed.
  const handleSellingPriceChange = (itemId, index, value) => {
    setItems(prev =>
      prev.map(item =>
        item.id !== itemId
          ? item
          : {
              ...item,
              ranges: item.ranges.map((r, idx) => (idx === index ? { ...r, selling_price: value } : { ...r })),
              // update vendor_details.vendor_price optimistically using current values (only > 0 count)
              vendor_details: {
                ...(item.vendor_details || {}),
                vendor_price: computeLowestSellingPrice(
                  item.ranges.map((r, idx) => (idx === index ? { ...r, selling_price: value } : r))
                ) ?? (item.vendor_details?.vendor_price ?? null),
              },
            }
      )
    );

    setChangedItems(prev => {
      const s = new Set(prev);
      s.add(itemId);
      return s;
    });
  };

  // Save is allowed only if the item was edited AND NOT all selling_price values are exactly zero
  const isAllSellingPricesZero = (ranges = []) => {
    if (!Array.isArray(ranges) || ranges.length === 0) return false; // no moqs -> Save hidden elsewhere
    return ranges.every(r => {
      // treat empty string as NOT zero
      if (r.selling_price === '' || r.selling_price == null) return false;
      const num = Number(r.selling_price);
      return Number.isFinite(num) && num === 0;
    });
  };

  const isSaveEnabledForItem = (item) => {
    if (!changedItems.has(item.id)) return false;
    // block save only when every selling_price is zero
    return !isAllSellingPricesZero(item.ranges);
  };

  const handleSave = async (item) => {
  try {
    // mark saving
    setSavingItems(prev => new Set(prev).add(item.id));

    const vendorDataString = localStorage.getItem("vendorData");
    if (!vendorDataString) throw new Error("Vendor data missing");
    const vendorData = JSON.parse(vendorDataString);

    const token = sessionStorage.getItem("token");
    if (!token) throw new Error("Token missing");

    let moqsPayload = [];
    let vendor_price = null;

    /* ---------------------------------------------------
       CASE 1 → PRODUCT HAS MOQ RANGES (item.ranges exists)
    ---------------------------------------------------- */
    if (Array.isArray(item.ranges) && item.ranges.length > 0) {
      moqsPayload = item.ranges.map(r => {
        const selling = r.selling_price ? Number(r.selling_price) : 0;

        return {
          min_quantity: Number(r.min_quantity) || 0,
          max_quantity: Number(r.max_quantity) || 0,
          selling_price: selling,
          cost_price: r.cost_price ? Number(r.cost_price) : 0,
          ...(r.created_at ? { created_at: r.created_at } : {})
        };
      });

      const lowestValid = computeLowestSellingPrice(item.ranges);
      vendor_price = lowestValid ?? item.vendor_details?.vendor_price ?? 0;
    }

    /* ---------------------------------------------------
       CASE 2 → PRODUCT HAS NO MOQ  (single price only)
    ---------------------------------------------------- */
    else {
      moqsPayload = []; // backend expects array
      vendor_price = item.single_price ? Number(item.single_price) : 0;
    }

    // Final payload
    const bodyData = {
      vendor_id: vendorData.id,
      vendor_price,
      moqs: moqsPayload,
      SKU_Code: item.SKU_Code || item.SKU || ""
    };

    const res = await fetch(
      `${fetchUrl}product/update-supplier-product-data?product_id=${item.id}&vendor_id=${vendorData.id}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "x-token": token
        },
        body: JSON.stringify(bodyData),
      }
    );

    const result = await res.json();

    if (!res.ok || !result.success) {
      throw new Error(result.message || "Update failed");
    }

    toast.success(`${item.product_name} price updated`);

    // remove highlight
    setChangedItems(prev => {
      const s = new Set(prev);
      s.delete(item.id);
      return s;
    });

  } catch (err) {
    console.error("Unified Save Error", err);
    toast.error(err.message || "Unable to save");
  } finally {
    // remove saving state
    setSavingItems(prev => {
      const s = new Set(prev);
      s.delete(item.id);
      return s;
    });
  }
};


  const isItemSaving = id => savingItems.has(id);

  /* ------------------------------ Render ------------------------------ */

  return (
  <div className="min-h-screen bg-[#F9FAFB]">
    <Toaster position="bottom-center" />

    <nav className="sticky top-0 z-50 flex items-center bg-white shadow-md px-1 h-14">
      <button onClick={() => navigate('/supplier/home')}>
        <img 
          src={ArrowLeft} 
          alt="Back" 
          className="w-12 h-8 mr-1"
        />
      </button>

      <div className="flex flex-col justify-center">
        <p className="text-lg font-semibold leading-none">
          Daily Price Update
        </p>
        <p className="text-xs font-medium leading-none mt-1">
          {categoryData?.product_category_name}
        </p>
      </div>
    </nav>

    <div className="p-4">
      {items.map(item => {
        const saving = isItemSaving(item.id);
        const canSave = isSaveEnabledForItem(item);

        return (
          <div
            key={item.id}
            className="bg-white rounded-xl shadow p-4 border border-gray-200 mb-4 relative transition-all duration-300"
          >
            {/* disable card while saving */}
            <div className={saving ? 'opacity-40 blur-sm pointer-events-none' : ''}>
              <div className="flex gap-4">

                {/* Unified product image block */}
              <div className="bg-gray-100 rounded-md p-2 h-20 w-20 flex items-center justify-center relative">
  {item.product_image ? (
    <img
      src={ImgUrl + item.product_image}
      alt={item.product_name}
      className="h-full w-full object-contain absolute"
      onError={(e) => {
        e.target.onerror = null;
        e.target.style.display = "none"; // hide broken image
      }}
    />
  ) : (
    <FontAwesomeIcon 
      icon={faImage} 
      className="text-gray-400 text-2xl absolute"
    />
  )}
</div>

                <div>
                  <h2 className="text-md font-semibold text-gray-800">{item.product_name}</h2>

                  <div className="flex mt-1">
                    <p className="text-xs text-gray-500">Last Updated Price:</p>
                    <p className="ml-2 text-xs font-medium text-gray-700">
                      {item.vendor_details?.vendor_price ? `₹${item.vendor_details.vendor_price}` : '-'}
                    </p>
                  </div>

                  {item.latest_updated_date ? (
                    <p className="mt-1 text-xs text-gray-500">
                      Last Updated Date: {item.latest_updated_date}
                    </p>
                  ) : null}
                </div>

              </div>

              {/* price ranges */}
              <div className="mt-4 space-y-3">
                {Array.isArray(item.ranges) && item.ranges.length > 0 ? (
                  item.ranges.map((range, idx) => (
                    <div
                      key={idx}
                      className="flex flex-row gap-3 items-center bg-gray-50 p-3 rounded-lg"
                    >
                      {/* Min Qty */}
                      <div className="relative">
                        <label className="text-[10px] text-gray-500 ml-1">Min Qty</label>
                        <input
                          type="number"
                          value={range.min_quantity}
                          readOnly
                          className="border rounded-md p-2 bg-gray-100 w-full text-sm"
                        />
                      </div>

                      {/* Divider */}
                      <div className="flex flex-col justify-center items-center">
                        <span className="text-xs text-gray-400 mt-4">to</span>
                      </div>

                      {/* Max Qty */}
                      <div className="relative">
                        <label className="text-[10px] text-gray-500 ml-1">Max Qty</label>
                        <input
                          type="number"
                          value={range.max_quantity}
                          readOnly
                          className="border rounded-md p-2 bg-gray-100 w-full text-sm"
                        />
                      </div>

                      {/* Selling Price */}
                      <div className="relative">
                        <label className="text-[10px] text-gray-500 ml-1">Price</label>

                        <span className="mt-1 absolute left-3 top-1/2 -translate-y-[2px] text-gray-500 text-sm">
                          ₹
                        </span>

                        <input
                          type="number"
                          value={range.selling_price}
                          onChange={e => handleSellingPriceChange(item.id, idx, e.target.value)}
                          className="border rounded-md p-2 pl-7 w-full text-sm"
                          placeholder="Price"
                        />
                      </div>
                    </div>
                  ))
                ) : (

               <div className="flex items-center justify-center gap-6 bg-gray-50 p-3 rounded-lg mt-2">

 <div className="relative w-[30%]">
  {/* Rupee Icon */}
  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
    ₹
  </span>

  {/* Input */}
  <input
    type="number"
    placeholder="Price"
    value={item.single_price || ""}
    onChange={(e) => {
      const value = e.target.value;

      setItems(prev =>
        prev.map(p =>
          p.id !== item.id
            ? p
            : {
                ...p,
                single_price: value,
                vendor_details: {
                  ...(p.vendor_details || {}),
                  vendor_price: value ? Number(value) : null
                },
              }
        )
      );

      setChangedItems(prev => {
        const s = new Set(prev);
        s.add(item.id);
        return s;
      });
    }}
    className="border border-gray-300 rounded-md px-6 py-2 text-sm 
               w-full text-center placeholder:text-center
               focus:ring-1 focus:ring-blue-500"
  />
</div>

  {/* SAVE BUTTON */}
  <button
  onClick={() => handleSave(item)}
  disabled={!item.single_price || savingItems.has(item.id) || !changedItems.has(item.id)}
  className={`px-4 py-2 rounded-md text-sm font-medium shadow-sm active:scale-95
    ${!item.single_price || savingItems.has(item.id) || !changedItems.has(item.id)
      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
      : "bg-red-500 text-white hover:bg-red-600"
    }`}
>
  {savingItems.has(item.id) ? "Saving..." : "Save"}
</button>


</div>

                )}
              </div>

              {/* Save button hidden if no ranges */}
              {Array.isArray(item.ranges) && item.ranges.length > 0 && (
                <div className="mt-4 flex justify-center">
                  <button
                    onClick={() => handleSave(item)}
                    disabled={!canSave}
                    className={`w-[60%] py-3 rounded-xl text-lg font-semibold ${
                      !canSave ? 'bg-gray-400 text-gray-200' : 'bg-red-500 text-white hover:bg-red-600'
                    }`}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              )}
            </div>

            {/* spinner overlay while saving */}
            {saving && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="h-8 w-8 border-4 border-gray-300 border-t-red-500 rounded-full animate-spin" />
              </div>
            )}
          </div>
        );
      })}
    </div>
  </div>
);

}
